import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalAsnDetail } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/inbound/$asnId")({
  component: AsnDetail,
});

function AsnDetail() {
  const { asnId } = Route.useParams();
  const fetchDetail = useServerFn(getPortalAsnDetail);
  const { data, isLoading, error } = useQuery({
    queryKey: ["portal-asn", asnId],
    queryFn: () => runServerFn(() => fetchDetail({ data: { asnId } }), "Loading ASN"),
  });

  return (
    <>
      <PortalPageHeader
        title={data?.asn?.reference ?? "ASN"}
        description="Advance shipment notice detail"
        actions={
          <Button asChild size="sm" variant="outline">
            <Link to="/portal/inbound"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link>
          </Button>
        }
      />
      <div className="page-container space-y-4">
        {isLoading ? <p className="text-muted-foreground">Loading…</p>
        : error ? <p className="text-destructive">{(error as Error).message}</p>
        : data ? (
          <>
            <Card className="p-4 grid sm:grid-cols-4 gap-4 text-sm">
              <div><div className="text-muted-foreground text-xs">Reference</div><div className="font-mono">{data.asn.reference}</div></div>
              <div><div className="text-muted-foreground text-xs">Carrier</div><div>{data.asn.carrier ?? "—"}</div></div>
              <div><div className="text-muted-foreground text-xs">Expected</div><div>{data.asn.expected_at ? new Date(data.asn.expected_at).toLocaleDateString() : "—"}</div></div>
              <div><div className="text-muted-foreground text-xs">Status</div><StatusPill tone={statusTone(data.asn.status)}>{data.asn.status}</StatusPill></div>
            </Card>
            <div className="border rounded-lg bg-card overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>SKU</TableHead><TableHead>Name</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead>Lot</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.lines.length > 0 ? data.lines.map((l: any) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-mono">{l.skus?.sku_code ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{l.skus?.name ?? "—"}</TableCell>
                      <TableCell className="text-right">{l.expected_qty}</TableCell>
                      <TableCell className="text-right">{l.received_qty}</TableCell>
                      <TableCell className="text-muted-foreground">{l.lot ?? "—"}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No lines recorded.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}